import { PaymentRequest, PaymentRequestItem } from '@/types/database/payment-requests';

const MAX_ITEMS_DISPLAY = 8;
const MAX_MESSAGE_LENGTH = 4000; // Leave buffer for Telegram's 4096 limit

/**
 * Escape special characters for Telegram MarkdownV1
 */
const escapeMarkdown = (text: string): string => {
  return text.replace(/([_*\[\]()~`>#+\-=|{}.!])/g, '\\$1');
};

/**
 * Format currency amount
 */
const formatAmount = (amount: number, currency: string = 'ETB'): string => {
  return `${currency} ${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/**
 * Format date with time
 */
const formatDateTime = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

/**
 * Format requester name
 */
export const getRequesterName = (request: PaymentRequest): string => {
  if (request.requester_first_name && request.requester_last_name) {
    return `${request.requester_first_name} ${request.requester_last_name}`;
  }
  if (request.requestor?.full_name) {
    return request.requestor.full_name;
  }
  return 'Unknown';
};

/**
 * Format items summary with smart truncation
 */
const formatItemsSummary = (items?: PaymentRequestItem[]): string => {
  if (!items || items.length === 0) return '';
  
  const displayItems = items.slice(0, MAX_ITEMS_DISPLAY);
  const remaining = items.length - MAX_ITEMS_DISPLAY;
  
  let itemsText = '*Items:*\n';
  displayItems.forEach((item, index) => {
    const qty = item.quantity || 1;
    const desc = item.description.length > 40 
      ? item.description.substring(0, 37) + '...' 
      : item.description;
    itemsText += `${index + 1}. ${escapeMarkdown(desc)} (${qty} × ${formatAmount(item.unit_price)})\n`;
  });
  
  if (remaining > 0) {
    itemsText += `_... and ${remaining} more item(s)_\n`;
  }
  
  return itemsText + '\n';
};

/**
 * Format notification for new payment request requiring approval
 */
export const formatNewPaymentApprovalNotification = (request: PaymentRequest): string => {
  const requesterName = getRequesterName(request);
  const departmentName = request.department?.name || 'N/A';
  const categoryName = request.category?.name || 'N/A';
  const itemsSummary = formatItemsSummary(request.items);
  
  let message = `📋 *New Payment Request*

*${escapeMarkdown(request.request_number)}*
━━━━━━━━━━━━━━━━━━━━

📝 *Title:* ${escapeMarkdown(request.title)}
💰 *Amount:* ${formatAmount(request.total_amount, request.currency)}
${request.vat_applicable ? `📊 *VAT:* ${formatAmount(request.vat_amount || 0, request.currency)}\n` : ''}👤 *Requester:* ${escapeMarkdown(requesterName)}
🏢 *Department:* ${escapeMarkdown(departmentName)}
📁 *Category:* ${escapeMarkdown(categoryName)}
${request.vendor_name ? `🏪 *Vendor:* ${escapeMarkdown(request.vendor_name)}\n` : ''}📅 *Submitted:* ${formatDateTime(request.submitted_at)}

${itemsSummary}${request.description ? `📄 *Description:*\n${escapeMarkdown(request.description)}\n\n` : ''}⏳ *Action Required:* Please review and approve/reject this request.`;

  // Truncate if message exceeds Telegram limit
  if (message.length > MAX_MESSAGE_LENGTH) {
    message = message.substring(0, MAX_MESSAGE_LENGTH - 50) + '\n\n_... message truncated_';
  }

  return message;
};

/**
 * Format notification for approved payment request
 */
export const formatPaymentApprovedNotification = (
  request: PaymentRequest, 
  approverName: string
): string => {
  return `✅ *Payment Request Approved*

*${escapeMarkdown(request.request_number)}*
━━━━━━━━━━━━━━━━━━━━

📝 *Title:* ${escapeMarkdown(request.title)}
💰 *Amount:* ${formatAmount(request.total_amount, request.currency)}

👤 *Approved by:* ${escapeMarkdown(approverName)}
📅 *Approved at:* ${request.approved_at ? new Date(request.approved_at).toLocaleDateString() : new Date().toLocaleDateString()}

Your payment request has been approved and is now pending payment.`;
};

/**
 * Format notification for rejected payment request
 */
export const formatPaymentRejectedNotification = (
  request: PaymentRequest, 
  reason: string,
  rejectorName?: string
): string => {
  return `❌ *Payment Request Rejected*

*${escapeMarkdown(request.request_number)}*
━━━━━━━━━━━━━━━━━━━━

📝 *Title:* ${escapeMarkdown(request.title)}
💰 *Amount:* ${formatAmount(request.total_amount, request.currency)}

${rejectorName ? `👤 *Rejected by:* ${escapeMarkdown(rejectorName)}\n` : ''}📅 *Rejected at:* ${new Date().toLocaleDateString()}

📋 *Reason:*
${escapeMarkdown(reason)}

Please review the feedback and submit a revised request if needed.`;
};

/**
 * Format notification for confirmed payment
 */
export const formatPaymentConfirmedNotification = (
  request: PaymentRequest, 
  paymentReference?: string
): string => {
  return `💳 *Payment Confirmed*

*${escapeMarkdown(request.request_number)}*
━━━━━━━━━━━━━━━━━━━━

📝 *Title:* ${escapeMarkdown(request.title)}
💰 *Amount:* ${formatAmount(request.total_amount, request.currency)}
${request.vendor_name ? `🏪 *Vendor:* ${escapeMarkdown(request.vendor_name)}\n` : ''}
📅 *Payment Date:* ${formatDateTime(request.actual_payment_date)}
🕒 *Confirmed at:* ${formatDateTime(request.paid_at)}
${paymentReference ? `🔖 *Reference:* ${escapeMarkdown(paymentReference)}\n` : ''}
✅ Your payment request has been processed successfully.`;
};

/**
 * Format notification for finance team about approved request
 */
export const formatFinanceApprovalNotification = (
  request: PaymentRequest,
  approverName: string
): string => {
  const requesterName = getRequesterName(request);
  const departmentName = request.department?.name || 'N/A';
  
  return `💼 *Payment Request Ready for Processing*

*${escapeMarkdown(request.request_number)}*
━━━━━━━━━━━━━━━━━━━━

📝 *Title:* ${escapeMarkdown(request.title)}
💰 *Amount:* ${formatAmount(request.total_amount, request.currency)}
${request.vat_applicable ? `📊 *VAT:* ${formatAmount(request.vat_amount || 0, request.currency)}\n` : ''}👤 *Requester:* ${escapeMarkdown(requesterName)}
🏢 *Department:* ${escapeMarkdown(departmentName)}
${request.vendor_name ? `🏪 *Vendor:* ${escapeMarkdown(request.vendor_name)}\n` : ''}🏦 *Payment Method:* ${request.payment_method || 'Not specified'}
${request.preferred_payment_date ? `📅 *Preferred Date:* ${new Date(request.preferred_payment_date).toLocaleDateString()}\n` : ''}✅ *Approved by:* ${escapeMarkdown(approverName)}

${request.vendor_bank_name ? `\n*Bank Details:*\n🏛️ Bank: ${escapeMarkdown(request.vendor_bank_name)}\n💳 Account: ${escapeMarkdown(request.vendor_bank_account || 'N/A')}\n📍 Branch: ${escapeMarkdown(request.vendor_bank_branch || 'N/A')}\n` : ''}
This request is approved and ready for payment.`;
};
